import React, { useState, useEffect } from 'react';
import { GasChromatographyAsset } from '../models/gas-chromatography-asset';
import { GasChromatographyInstrumentLookupData } from '../models/gas-chromatography-instrument-lookup-data';
import { GasChromatographyBenchSheet } from '../models/gas-chromatography-bench-sheet';
import { ItemBasicInformation } from '../models/item-basic-information';
import { NumberBoxComponent } from './NumberBoxComponent';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import { BenchSheetTechnologyType } from '../models/enums';
import { CrudFunctions } from '../utilities/crud-functions';
import { FieldUpdate } from '../models/field-update';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import SecondaryBenchSheetInstrument from './SecondaryBenchSheetInstrument';

import CheckBox from 'devextreme-react/check-box';
import Button from 'devextreme-react/button';
import { Popup as DialogPopup } from 'devextreme-react/popup';
import RadioGroup from 'devextreme-react/radio-group';

/*
direct all technology-specific (GC or GCRS) to respective tables in READ and UPDATES
*/

const BENCH_SHEETS_ENTITY = "BenchSheets";
const GAS_CHROMATOGRAPHY_BENCH_SHEETS_ENTITY = "GasChromatographyBenchSheets";
const GAS_CHROMATOGRAPHY_RESIDUAL_SOLVENTS_BENCH_SHEETS_ENTITY = "GasChromatographyResidualSolventsBenchSheets";

//temp
const injectionVolumeUnits = [
    { id: 1, unit: 'mL' },
    { id: 9, unit: 'µL' }
];

//temp
const detectorTypes = [
    { id: 1, name: 'Front Detector' },
    { id: 2, name: 'Back Detector' }
];

export interface Props {
    readOnly?: boolean;
    benchSheetId: string;
    technology: BenchSheetTechnologyType;
}

export default function GasChromatographyInstrument(props: Props) {
    const [benchSheet, setBenchSheet] =
        useState<GasChromatographyBenchSheet>();

    const [instruments, setInstruments] =
        useState<GasChromatographyAsset[]>();

    const [selectedInstrument, setSelectedInstrument] =
        useState<GasChromatographyAsset>();

    const [gasChromatographyInstrumentLookupData, setGasChromatographyInstrumentLookupData] =
        useState<GasChromatographyInstrumentLookupData>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    // const [dataLoaded, setDataLoaded] =
    //   useState<boolean>();

    useEffect(() => {
        getAllData();
    }, []);

    useEffect(() => {
        getBenchSheet();
    }, [props.benchSheetId]);

    useEffect(() => {
        getMatchingInstrument();
    }, [benchSheet, instruments]);

    let instrument = selectedInstrument;

    let inletTemperatureComponent = <NumberBoxComponent id="inletTemperature" readOnly={props.readOnly}
        cid="D1758DF0-10BC-42E9-9CD3-D500009B12D7"
        fixedUnit={'°C'} onValueChanged={onInstrumentValueChanged}
        value={benchSheet ? benchSheet.inletTemperature : undefined} />;

    let detectorTemperatureComponent = <NumberBoxComponent id="detectorTemperature"
        cid="7387B65F-3EE9-4A42-AB99-2E60C28A1EF8" readOnly={props.readOnly}
        fixedUnit={'°C'} onValueChanged={onInstrumentValueChanged}
        value={benchSheet ? benchSheet.detectorTemperature : undefined} />;

    let ratioComponent = <div className="pw-ratio-container">
        <NumberBoxComponent id="inletModeRatio1" readOnly={props.readOnly}
            cid="A5D0206D-2BC7-4A6C-8526-EDAF5219D79C"
            format="###0"
            onValueChanged={onInstrumentValueChanged}
            containerClass='pw-ratio-textbox1'
            value={benchSheet ? benchSheet.inletModeRatio1 : undefined} />

        <div className="pw-readonly-label">:</div>
        <NumberBoxComponent id="inletModeRatio2" readOnly={props.readOnly}
            cid="BBD7E117-E22A-40E0-8DCD-F7386AF9BB82"
            format="###0"
            containerClass='pw-ratio-textbox2'
            onValueChanged={onInstrumentValueChanged}
            value={benchSheet ? benchSheet.inletModeRatio2 : undefined} />
    </div>;

    let transferLineTemperatureComponent = <NumberBoxComponent id="transferLineTemperature"
        cid="9131D58D-19F9-4F65-8CED-8975E7D3395C" readOnly={props.readOnly}
        format="#0.0" fixedUnit={'°C'}
        onValueChanged={onInstrumentValueChanged}
        value={benchSheet ? benchSheet.transferLineTemperature : undefined} />;

    return (
        <div>
            <div className="lq-header pw-margin-top-05 aurum-light-blue-bg">
                <div className="lq-header-title">GC Instrument</div>
                <div className="title-bar-spacer" />
                <div>
                    <Button className="title-bar-button"
                        text="" icon="info" type="success"
                        hint="item information"
                        onClick={showInformationDialog} />
                </div>
            </div>

            <SecondaryBenchSheetInstrument
                benchSheetId={props.benchSheetId}
                isReadOnly={props.readOnly}
                technology={props.technology}
                showHeader={false}
            />

            {/*TODO: sara get fields to line up better*/}
            <div className="pw-form-section">
                <div className="pw-comp-row">
                    <div className="pw-form-unit pw-comp-15">
                        <div className="pw-form-unit-label">Injection Volume</div>
                        <NumberBoxComponent id="injectionVolume"
                            unitId="injectionVolumeUom" readOnly={props.readOnly}
                            showUnits={true}
                            cid="4D43E433-2CDC-41EC-9DFD-6786ADEDADEE"
                            uoms={injectionVolumeUnits}
                            value={benchSheet && benchSheet.injectionVolume}
                            selectedUom={benchSheet &&
                                benchSheet.injectionVolumeUom ?
                                benchSheet.injectionVolumeUom : 0}
                            onUomChanged={onInjectionVolumeUomChanged}
                            onValueChanged={onInstrumentValueChanged} />
                    </div>

                    <div className="pw-form-unit pw-comp-15 pw-margin-left-1">
                        <div className="pw-form-unit-label">Carrier Gas</div>
                        <div className="pw-readonly-label">
                            {instrument?.carrierGas?.description ??
                                instrument?.carrierGas?.name ?? 'NA'}</div>
                    </div>

                    {instrument && instrument.otherDetector &&
                        <div>
                            <CheckBox id="checktunePassed" text="Checktune Passed" className="pw-form-unit-label"
                                onValueChanged={onInstrumentValueChanged} readOnly={props.readOnly}
                                value={benchSheet && benchSheet.checktunePassed} />
                        </div>
                    }

                </div>

                <div className="pw-gc-asset-config">
                    <div className="pw-section-right-margin-1">
                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">Front Inlet</div>
                                <div className="pw-readonly-label">{instrument &&
                                    instrument.frontInlet && instrument.frontInlet.name ?
                                    instrument.frontInlet.name : 'NA'}</div>
                            </div>
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-15 pw-comp-row-align-bottom">
                                <label className="pw-form-unit-label">Inlet Temperature</label>
                                {inletTemperatureComponent}
                            </div>
                        </div>

                        {instrument && instrument.frontInlet &&
                            instrument.frontInlet.id !== 2 &&
                            <div>
                                <div className="pw-comp-row">
                                    <div className="pw-form-unit pw-comp-15">
                                        <div className="pw-form-unit-label">Inlet Mode</div>
                                        <RadioGroup items={gasChromatographyInstrumentLookupData &&
                                            gasChromatographyInstrumentLookupData.gcInletModes ?
                                            gasChromatographyInstrumentLookupData.gcInletModes : []}
                                            displayExpr={'name'} valueExpr={'id'}
                                            layout="horizontal" readOnly={props.readOnly}
                                            onValueChanged={onInletModeChanged}
                                            value={benchSheet &&
                                                benchSheet.inletMode ?
                                                benchSheet.inletMode : 1} />
                                    </div>
                                </div>

                                {benchSheet &&
                                    benchSheet.inletMode &&
                                    benchSheet.inletMode === 1 &&
                                    <div className="pw-comp-row pw-margin-top-05">
                                        <div className="pw-form-unit pw-comp-15">
                                            <div className="pw-form-unit-label">Ratio</div>
                                            {ratioComponent}
                                        </div>
                                    </div>
                                }
                            </div>
                        }
                    </div>

                    {instrument && instrument.otherDetector &&
                        <div>
                            <div>
                                <div className="pw-comp-row">
                                    <div className="pw-form-unit pw-comp-15">
                                        <div className="pw-form-unit-label">Other Detector</div>
                                        <div className="pw-readonly-label">{instrument &&
                                            instrument.otherDetector ?
                                            instrument.otherDetector : 'NA'}</div>
                                    </div>
                                </div>

                                <div className="pw-comp-row">
                                    <div className="pw-form-unit pw-comp-15 pw-comp-row-align-bottom">
                                        <label className="pw-form-unit-label">Transfer Line Temperature</label>
                                        {transferLineTemperatureComponent}
                                    </div>
                                </div>

                                {/*
                                                <div className="pw-comp-row">
                                                    <div className="pw-form-unit pw-comp-15">
                                                        <div className="pw-form-unit-label">Ion Source Temperature</div>
                                                        <div className="pw-readonly-label">{instrument &&
                                                            instrument.ionSourceTemperature ?
                                                            instrument.ionSourceTemperature : 'NA'}°C</div>
                                                    </div>
                                                </div>
                                                <div className="pw-comp-row">
                                                    <div className="pw-form-unit pw-comp-15">
                                                        <div className="pw-form-unit-label">Quadrapole Temperature</div>
                                                        <div className="pw-readonly-label">{instrument &&
                                                            instrument.quadrapoleTemperature ?
                                                            instrument.quadrapoleTemperature : 'NA'}°C</div>
                                                    </div>
                                                </div>
                                                */}
                            </div>
                        </div>
                    }

                    {instrument && !instrument.otherDetector &&
                        <div>
                            <div className="pw-comp-row">
                                <div className="pw-form-unit pw-comp-30">
                                    <div className="pw-form-unit-label">Detector Type</div>
                                    <RadioGroup items={detectorTypes ? detectorTypes : []}
                                        displayExpr={'name'} valueExpr={'id'}
                                        layout="horizontal" readOnly={props.readOnly}
                                        onValueChanged={onDetectorChanged}
                                        value={benchSheet &&
                                            benchSheet.detectorType ?
                                            benchSheet.detectorType : 1} />
                                </div>
                            </div>

                            {benchSheet && benchSheet.detectorType &&
                                benchSheet.detectorType === 1 &&
                                <div>
                                    <div className="pw-comp-row">
                                        <div className="pw-form-unit pw-comp-15 pw-margin-top-05">
                                            <div className="pw-form-unit-label">Type</div>
                                            <div className="pw-readonly-label">{instrument &&
                                                instrument.frontDetector &&
                                                instrument.frontDetector.name ?
                                                instrument.frontDetector.name : 'NA'}</div>
                                        </div>
                                    </div>

                                    {instrument && instrument.frontDetector &&
                                        <div className="pw-comp-row">
                                            <div className="pw-form-unit pw-comp-15 pw-comp-row-align-bottom">
                                                <label className="pw-form-unit-label">Detector Temperature</label>
                                                {detectorTemperatureComponent}
                                            </div>
                                        </div>
                                    }
                                </div>
                            }

                            {benchSheet &&
                                benchSheet.detectorType &&
                                benchSheet.detectorType === 2 &&
                                <div>
                                    <div className="pw-comp-row">
                                        <div className="pw-form-unit pw-comp-15">
                                            <div className="pw-form-unit-label">Type</div>
                                            <div className="pw-readonly-label">{instrument &&
                                                instrument.backDetector &&
                                                instrument.backDetector.name ?
                                                instrument.backDetector.name : 'NA'}</div>
                                        </div>
                                    </div>

                                    {instrument && instrument.backDetector &&
                                        <div className="pw-comp-row">
                                            <div className="pw-form-unit pw-comp-15 pw-comp-row-align-bottom">
                                                <label className="pw-form-unit-label">Detector Temperature</label>
                                                {detectorTemperatureComponent}
                                            </div>
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                    }
                </div>

                {itemBasicInformationDialogVisible &&
                    <DialogPopup visible={itemBasicInformationDialogVisible}
                        onHiding={hideItemBasicInformationDialog}
                        title="Item Basic Information"
                        width={'25rem'}
                        height={'21rem'}
                        dragEnabled={false}
                        resizeEnabled={false}>
                        <ItemBasicInformationDialog initInformation={itemBasicInformation}
                            onOkClick={hideItemBasicInformationDialog} />
                    </DialogPopup>
                }
            </div>

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }
        </div>
    );

    function getMatchingInstrument() {

        console.log("getMatchingInstrument()");
        console.log("instruments");
        console.log(instruments);

        console.log("benchSheet:");
        console.log(benchSheet);


        if (instruments && benchSheet && benchSheet.instrumentId) {
            const matchingInstrument = instruments.find(i => i.assetId === benchSheet.instrumentId);

            console.log("IN HERE at 710");
            console.log(matchingInstrument);


            if (matchingInstrument)
                setSelectedInstrument(matchingInstrument);
        }
    }

    function onInletTypeChanged(e: any) {
        //if (!dataLoaded)
        //   return;

        if (e.element) {
            e.element.id = "inletType";

            onInstrumentValueChanged(e);
        }
    }

    function onInletModeChanged(e: any) {
        //if (!dataLoaded)
        //  return;

        if (e.element) {
            e.element.id = "inletMode";

            onInstrumentValueChanged(e);
        }
    }

    function onDetectorChanged(e: any) {
        //if (!dataLoaded)
        //   return;

        if (e.element) {
            e.element.id = "detectorType";

            onInstrumentValueChanged(e);
        }
    }

    async function onInjectionVolumeUomChanged(e: any) {
        //if (!dataLoaded)
        //   return;

        const val = e.selectedItem && e.selectedItem.id ? e.selectedItem.id : null;

        setBenchSheet({ ...benchSheet, "injectionVolumeUom": val });

        let fieldUpdate: FieldUpdate = {
            entity: props.technology === BenchSheetTechnologyType.GasChromatography ?
                GAS_CHROMATOGRAPHY_BENCH_SHEETS_ENTITY : GAS_CHROMATOGRAPHY_RESIDUAL_SOLVENTS_BENCH_SHEETS_ENTITY,
            field: "injectionVolumeUom",
            value: val,
            rowId: props.benchSheetId,
        };

        await updateGcBenchSheetField(fieldUpdate)
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Updating Bench Sheet Field");
            });
    }

    function showInformationDialog() {
        if (instrument) {
            let itemBasicInitInformation: ItemBasicInformation = {
                rowPointer: instrument.assetId,
                createTimestamp: instrument.createTimestamp,
                lastModifiedTimestamp: instrument.modifyTimestamp,
                createUser: instrument.createUser,
                modifyUser: instrument.modifyUser
            };

            setItemBasicInformation(itemBasicInitInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    async function onInstrumentChanged(e: any) {
        console.log("onInstrumentChanged");
        console.log(e);

        let newAssetId = e.value;

        /*
        if (benchSheet && benchSheet.instrumentId &&
            benchSheet.instrumentId === newAssetId)
            return;
        */

        console.log("AT 564");

        if (instruments) {
            let matchingInstrument = instruments.find(i => i.assetId === newAssetId);

            console.log("matchingInstrument:");
            console.log(matchingInstrument);


            if (matchingInstrument) {
                setSelectedInstrument(matchingInstrument);

                updateField(BENCH_SHEETS_ENTITY, "instrumentId", newAssetId)
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating Bench Sheet Field");
                    });
            }
        }
    }

    async function onInstrumentValueChanged(e: any) {
        //if (!dataLoaded)
        //   return;

        let caller = '';

        if (e.event && e.event.data && e.event.data.prop)
            caller = e.event.data.prop;
        else
            caller = e.element.id;

        if (caller && benchSheet) {
            setBenchSheet({ ...benchSheet, [caller]: e.value });

            await updateField(GAS_CHROMATOGRAPHY_BENCH_SHEETS_ENTITY, caller, e.value)
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Updating Bench Sheet Field");
                });

            /*
            if (caller === "inletType") {
                setBenchSheet({ ...benchSheet, inletTemperature: 0 });

                await updateField(GAS_CHROMATOGRAPHY_BENCH_SHEETS_ENTITY, "inletTemperature", 0)
                    .catch((error: ResponseError) => {
                        XXX.publish(ModalSubscriberTopic, { modalType: ApiResponseErrorModal, responseError: error });
                    });

                setBenchSheet({ ...benchSheet, inletModeRatio1: 0 });

                await updateField(GAS_CHROMATOGRAPHY_BENCH_SHEETS_ENTITY, "inletModeRatio1", 0)
                    .catch((error: ResponseError) => {
                        XXX.publish(ModalSubscriberTopic, { modalType: ApiResponseErrorModal, responseError: error });
                    });

                setBenchSheet({ ...benchSheet, inletModeRatio2: 0 });

                await updateField(GAS_CHROMATOGRAPHY_BENCH_SHEETS_ENTITY, "inletModeRatio2", 0)
                    .catch((error: ResponseError) => {
                        XXX.publish(ModalSubscriberTopic, { modalType: ApiResponseErrorModal, responseError: error });
                    });
            }
            */
        }
    }

    async function updateField(entity: string, fieldName: string, value: any) {
        // if (!dataLoaded)
        //  return;

        let fieldUpdate: FieldUpdate = {
            entity: entity,
            field: fieldName,
            value: value,
            rowId: props.benchSheetId,
        };

        await updateGcBenchSheetField(fieldUpdate)
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Updating Bench Sheet Field");
            });
    }

    // CRUD methods

    async function getBenchSheet() {
        //const url = props.technology === BenchSheetTechnologyType.GasChromatography ? "gcbenchsheet" : "gcrsbenchsheet";
        //await CrudFunctions.getData(`benchsheets/${url}/${props.benchSheetId}`)

        await CrudFunctions.getData(`benchsheets/benchsheet/${props.benchSheetId}`)
            .then(data => {

                console.log("GC BS:")
                console.log(data);

                setBenchSheet(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Bench Sheet");
            });
    }

    async function getInstruments() {
        await CrudFunctions.getData("benchsheets/gcinstruments")
            .then(data => {

                console.log("INSTRUMENTS:");
                console.log(data);



                setInstruments(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Instruments");
            });
    }

    async function getInstrumentEnumerations() {
        await CrudFunctions.getData("benchsheets/gcinstrumentlookupdata")
            .then(data => {
                setGasChromatographyInstrumentLookupData(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Instrument Enumerations");
            });
    }

    //updates

    async function updateGcBenchSheetField(fieldUpdate: FieldUpdate) {
        return await CrudFunctions.putData("benchsheets/gcbenchsheetfield", fieldUpdate);
    }

    async function updateGcRsBenchSheetField(fieldUpdate: FieldUpdate) {
        return await CrudFunctions.putData("benchsheets/gcrsbenchsheetfield", fieldUpdate);
    }

    /*
    async function getAllData() {
        if (benchSheet && gasChromatographyInstrumentLookupData && instruments) {

            await Promise.all([
                getInstrumentEnumerations(),
                getInstruments(),
            ])
                .then(() => {
                    getMatchingInstrument();
                    setDataLoaded(true);
                });
        }
    }
    */

    async function getAllData() {
        await Promise.all([
            getInstrumentEnumerations(),
            getInstruments()
        ])
    }

    //show/hide

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    //other

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}

